import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import './GlobalConfigurationPage.css';
import apiService from '../../services/apiService';
import Loader from '../../CommanService/Loader';
import { PermissionGuard } from '../../components/PermissionGuard';
import { hasPermission, PERMISSION_CODES } from '../../utils/permissionUtils';

const GlobalConfigurationPage = () => {
  const { user } = useSelector(state => state.auth);
  const [selectedPartType, setSelectedPartType] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [stockLevels, setStockLevels] = useState({
    underStock: { q1: '', q2: '', q3: '', q4: '' },
    adequatelyStocked: { q1: '', q2: '', q3: '', q4: '' },
    overStock: { q1: '', q2: '', q3: '', q4: '' },
    limits: { percentageMin: '', percentageMax: '', priceMin: '', priceMax: '' }
  });
  const [originalStockLevels, setOriginalStockLevels] = useState({
    underStock: { q1: '', q2: '', q3: '', q4: '' },
    adequatelyStocked: { q1: '', q2: '', q3: '', q4: '' },
    overStock: { q1: '', q2: '', q3: '', q4: '' },
    limits: { percentageMin: '', percentageMax: '', priceMin: '', priceMax: '' }
  });
  const [partTypes, setPartTypes] = useState([]);
  const [loadingPartTypes, setLoadingPartTypes] = useState(false);
  const [priceLineConfigData, setPriceLineConfigData] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [minimumDataPoints, setMinimumDataPoints] = useState('');
  const [mlModelType, setMlModelType] = useState('');
  const [mlModelTypeId, setMlModelTypeId] = useState('');
  const [modelTypes, setModelTypes] = useState([]);
  const [loadingModelTypes, setLoadingModelTypes] = useState(false);

  // Permission checks
  const canEditGlobalConfig = hasPermission(user, PERMISSION_CODES.GLOBLE_CONFIG_EDIT);

  // Helper to apply part type selection and populate levels (ENGINE/TRANSMISSION)
  const applyPartTypeSelection = (value, sourceItems, availableModelTypes = modelTypes) => {
    const items = Array.isArray(sourceItems) ? sourceItems : priceLineConfigData;
    const stockStatusMap = {
      'understocked': 'underStock',
      'adequately stocked': 'adequatelyStocked',
      'overstocked': 'overStock'
    };
    const newLevels = {
      underStock: { q1: '', q2: '', q3: '', q4: '' },
      adequatelyStocked: { q1: '', q2: '', q3: '', q4: '' },
      overStock: { q1: '', q2: '', q3: '', q4: '' },
      limits: { percentageMin: '', percentageMax: '', priceMin: '', priceMax: '' }
    };
    const filtered = (items || []).filter(item => item?.part_type === value);
    filtered.forEach(item => {
      const status = (item.stock_status || '').toLowerCase();
      const qName = (item.quartile_name || '').toLowerCase();
      if (status === 'understocked' && /^q[1-4]$/.test(qName)) newLevels.underStock[qName] = item.quartile_value;
      if (status === 'adequately stocked' && /^q[1-4]$/.test(qName)) newLevels.adequatelyStocked[qName] = item.quartile_value;
      if (status === 'overstocked' && /^q[1-4]$/.test(qName)) newLevels.overStock[qName] = item.quartile_value;
      if (status === 'percentage' && qName === 'percentage_min') newLevels.limits.percentageMin = item.quartile_value;
      if (status === 'percentage' && qName === 'percentage_max') newLevels.limits.percentageMax = item.quartile_value;
      if (status === 'price' && qName === 'price_min') newLevels.limits.priceMin = item.quartile_value;
      if (status === 'price' && qName === 'price_max') newLevels.limits.priceMax = item.quartile_value;
      // Handle Minimum Data Points For Regration
      if (status === 'regration' && qName === 'minimum data points for regration') {
        setMinimumDataPoints(item.quartile_value || '');
      }
      // Handle ML Model Type
      if (status === 'model' && qName === 'model_type') {
        setMlModelTypeId(item.quartile_value || '');
        // Find the model name by ID
        const model = availableModelTypes.find(m => m.id === item.quartile_value);
        setMlModelType(model ? model.model_name : '');
      }
    });
    setSelectedPartType(value);
    setStockLevels(newLevels);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoadingModelTypes(true);
      setLoadingPartTypes(true);
      
      try {
        // First, fetch model types
        const modelRes = await apiService.GetAllModelType();
        const modelTypes = modelRes?.data || [];
        setModelTypes(modelTypes);
        setLoadingModelTypes(false);
        
        // Then, fetch price line config
        const configRes = await apiService.GetPriceLineConfig();
        const items = configRes?.data || [];
        const uniquePartTypes = Array.from(
          new Set(
            items
              .map(item => item?.part_type)
              .filter(Boolean)
          )
        ).sort();
        setPartTypes(uniquePartTypes);
        setPriceLineConfigData(items);
        
        // Select default part type: 'ENGINE' if available, else first available
        const defaultSelection = uniquePartTypes.includes('ENGINE')
          ? 'ENGINE'
          : (uniquePartTypes[0] || '');
        if (defaultSelection) {
          applyPartTypeSelection(defaultSelection, items, modelTypes);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        setPartTypes([]);
        setModelTypes([]);
      } finally {
        setLoadingPartTypes(false);
      }
    };

    fetchData();
  }, []);

  const handlePartTypeChange = (e) => {
    const value = e.target.value;
    applyPartTypeSelection(value);
  };

  // Validation functions
  const validatePercentageMin = (value) => {
    const numValue = parseFloat(value);
    if (value === '' || isNaN(numValue)) return '';
    if (numValue < -100 || numValue > 0) {
      return 'Percentage must be between -100 and 0';
    }
    return '';
  };

  const validatePercentageMax = (value) => {
    const numValue = parseFloat(value);
    if (value === '' || isNaN(numValue)) return '';
    if (numValue < 0 || numValue > 100) {
      return 'Percentage must be between 0 and 100';
    }
    return '';
  };

  const validatePriceMin = (value) => {
    const numValue = parseFloat(value);
    if (value === '' || isNaN(numValue)) return '';
    if (numValue < -500 || numValue > 0) {
      return 'Price must be between -500 and 0';
    }
    return '';
  };

  const validatePriceMax = (value) => {
    const numValue = parseFloat(value);
    if (value === '' || isNaN(numValue)) return '';
    if (numValue < 0 || numValue > 500) {
      return 'Price must be between 0 and 500';
    }
    return '';
  };

  const validateQuarterValue = (value) => {
    const numValue = parseFloat(value);
    if (value === '' || isNaN(numValue)) return '';
    if (numValue < -100 || numValue > 100) {
      return 'Value must be between -100 and 100';
    }
    return '';
  };

  const validateMinimumDataPoints = (value) => {
    const numValue = parseFloat(value);
    if (value === '' || isNaN(numValue)) return '';
    if (numValue < 0 || numValue > 5 || !Number.isInteger(numValue)) {
      return 'Must be an integer between 0 and 5';
    }
    return '';
  };

  const handleStockLevelChange = (level, quarter, value) => {
    setStockLevels(prev => ({
      ...prev,
      [level]: {
        ...prev[level],
        [quarter]: value
      }
    }));

    // Validate specific fields
    let errorMessage = '';
    if (level === 'limits') {
      if (quarter === 'percentageMin') {
        errorMessage = validatePercentageMin(value);
      } else if (quarter === 'percentageMax') {
        errorMessage = validatePercentageMax(value);
      } else if (quarter === 'priceMin') {
        errorMessage = validatePriceMin(value);
      } else if (quarter === 'priceMax') {
        errorMessage = validatePriceMax(value);
      }
    } else if (['underStock', 'adequatelyStocked', 'overStock'].includes(level)) {
      // Validate quarter values (Q1, Q2, Q3, Q4)
      if (['q1', 'q2', 'q3', 'q4'].includes(quarter)) {
        errorMessage = validateQuarterValue(value);
      }
    }

    // Update validation errors
    setValidationErrors(prev => ({
      ...prev,
      [`${level}_${quarter}`]: errorMessage
    }));
  };

  const handleEdit = () => {
    setOriginalStockLevels({ ...stockLevels });
    setIsEditing(true);
  };

  const handleSave = async () => {
    // Check for validation errors before saving
    const hasErrors = Object.values(validationErrors).some(error => error !== '');
    if (hasErrors) {
      alert('Please fix validation errors before saving.');
      return;
    }

    try {
      const stockStatusMap = {
        'understocked': 'underStock',
        'adequately stocked': 'adequatelyStocked',
        'overstocked': 'overStock'
      };
      // Only update items for the selected part type (ENGINE or TRANSMISSION)
      const updatedAll = (priceLineConfigData || []).map(item => {
        if (!selectedPartType) return item;
        if (item?.part_type !== selectedPartType) return item;
        // Patch quartile values for stock statuses
        if (item?.stock_status) {
          const statusKey = stockStatusMap[(item?.stock_status || '').toLowerCase()];
          const quarterKey = (item?.quartile_name || '').toLowerCase();
          if (statusKey && ['q1','q2','q3','q4'].includes(quarterKey)) {
            const editedValue = stockLevels?.[statusKey]?.[quarterKey];
            return {
              ...item,
              quartile_value: editedValue === '' || editedValue === null || editedValue === undefined ? '' : Number(editedValue)
            };
          }
          // Patch limits for percentage/price
          if (item.stock_status.toLowerCase() === 'percentage') {
            if (item.quartile_name.toLowerCase() === 'percentage_min') {
              return { ...item, quartile_value: stockLevels.limits.percentageMin === '' ? '' : Number(stockLevels.limits.percentageMin) };
            }
            if (item.quartile_name.toLowerCase() === 'percentage_max') {
              return { ...item, quartile_value: stockLevels.limits.percentageMax === '' ? '' : Number(stockLevels.limits.percentageMax) };
            }
          }
          if (item.stock_status.toLowerCase() === 'price') {
            if (item.quartile_name.toLowerCase() === 'price_min') {
              return { ...item, quartile_value: stockLevels.limits.priceMin === '' ? '' : Number(stockLevels.limits.priceMin) };
            }
            if (item.quartile_name.toLowerCase() === 'price_max') {
              return { ...item, quartile_value: stockLevels.limits.priceMax === '' ? '' : Number(stockLevels.limits.priceMax) };
            }
          }
          // Patch Minimum Data Points For Regration
          if (item.stock_status.toLowerCase() === 'regration' && item.quartile_name.toLowerCase() === 'minimum data points for regration') {
            return { ...item, quartile_value: minimumDataPoints === '' ? '' : Number(minimumDataPoints) };
          }
          // Patch ML Model Type
          if (item.stock_status.toLowerCase() === 'model' && item.quartile_name.toLowerCase() === 'model_type') {
            return { ...item, quartile_value: mlModelTypeId === '' ? '' : Number(mlModelTypeId) };
          }
        }
        return item;
      });
      // Only send ENGINE and TRANSMISSION as requested
      const payload = updatedAll.filter(x => x?.part_type === 'ENGINE' || x?.part_type === 'TRANSMISSION');
      await apiService.UpdatePriceLineConfig(payload);
      setPriceLineConfigData(updatedAll);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update price line configuration:', error);
    }
  };

  const handleMinimumDataPointsChange = (value) => {
    setMinimumDataPoints(value);
    const errorMessage = validateMinimumDataPoints(value);
    setValidationErrors(prev => ({
      ...prev,
      minimumDataPoints: errorMessage
    }));
  };

  const handleMlModelTypeChange = (value) => {
    setMlModelType(value);
    // Find the model ID by name
    const model = modelTypes.find(m => m.model_name === value);
    setMlModelTypeId(model ? model.id : '');
  };

  const handleCancel = () => {
    setStockLevels({ ...originalStockLevels });
    setValidationErrors({});
    setIsEditing(false);
    // Reset minimum data points to original value from API
    const currentPartTypeData = (priceLineConfigData || []).filter(item => 
      item?.part_type === selectedPartType && 
      item?.stock_status?.toLowerCase() === 'regration' && 
      item?.quartile_name?.toLowerCase() === 'minimum data points for regration'
    );
    if (currentPartTypeData.length > 0) {
      setMinimumDataPoints(currentPartTypeData[0].quartile_value || '');
    } else {
      setMinimumDataPoints('');
    }
    
    // Reset ML model type to original value from API
    const currentModelData = (priceLineConfigData || []).filter(item => 
      item?.part_type === selectedPartType && 
      item?.stock_status?.toLowerCase() === 'model' && 
      item?.quartile_name?.toLowerCase() === 'model_type'
    );
    if (currentModelData.length > 0) {
      setMlModelTypeId(currentModelData[0].quartile_value || '');
      const model = modelTypes.find(m => m.id === currentModelData[0].quartile_value);
      setMlModelType(model ? model.model_name : '');
    } else {
      setMlModelTypeId('');
      setMlModelType('');
    }
  };

  return (
    <div className="global-configuration-page">
      {(loadingPartTypes || loadingModelTypes) && <Loader />}
      <div className="configuration-content">
        {/* Select Part Type Section */}
        <div className="select-type-section">
          <label className="select-type-label">Select Part Type</label>
          <select 
            className="select-type-dropdown"
            value={selectedPartType}
            onChange={handlePartTypeChange}
          >
            <option value="">Select a part type...</option>
            {partTypes.map((type, index) => (
              <option key={index} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* Under Stock Section */}
        <div className="stock-section">
          <label className="stock-section-label">Under Stock</label>
          <div className="quarter-inputs">
            <div className="quarter-input-group">
              <label>Q1%</label>
              <input 
                type="number" 
                value={stockLevels.underStock.q1}
                onChange={(e) => handleStockLevelChange('underStock', 'q1', e.target.value)}
                placeholder="0"
                min="-100"
                max="100"
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.underStock_q1 ? 'input-error' : ''}
              />
              {validationErrors.underStock_q1 && (
                <span className="validation-error">{validationErrors.underStock_q1}</span>
              )}
            </div>
            <div className="quarter-input-group">
              <label>Q2%</label>
              <input 
                type="number" 
                value={stockLevels.underStock.q2}
                onChange={(e) => handleStockLevelChange('underStock', 'q2', e.target.value)}
                placeholder="0"
                min="-100"
                max="100"
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.underStock_q2 ? 'input-error' : ''}
              />
              {validationErrors.underStock_q2 && (
                <span className="validation-error">{validationErrors.underStock_q2}</span>
              )}
            </div>
            <div className="quarter-input-group">
              <label>Q3%</label>
              <input 
                type="number" 
                value={stockLevels.underStock.q3}
                onChange={(e) => handleStockLevelChange('underStock', 'q3', e.target.value)}
                placeholder="0"
                min="-100"
                max="100"
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.underStock_q3 ? 'input-error' : ''}
              />
              {validationErrors.underStock_q3 && (
                <span className="validation-error">{validationErrors.underStock_q3}</span>
              )}
            </div>
            <div className="quarter-input-group">
              <label>Q4%</label>
              <input 
                type="number" 
                value={stockLevels.underStock.q4}
                onChange={(e) => handleStockLevelChange('underStock', 'q4', e.target.value)}
                placeholder="0"
                min="-100"
                max="100"
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.underStock_q4 ? 'input-error' : ''}
              />
              {validationErrors.underStock_q4 && (
                <span className="validation-error">{validationErrors.underStock_q4}</span>
              )}
            </div>
          </div>
        </div>

        {/* Adequately Stocked Section */}
        <div className="stock-section">
          <label className="stock-section-label">Adequately Stocked</label>
          <div className="quarter-inputs">
            <div className="quarter-input-group">
              <label>Q1%</label>
              <input 
                type="number" 
                value={stockLevels.adequatelyStocked.q1}
                onChange={(e) => handleStockLevelChange('adequatelyStocked', 'q1', e.target.value)}
                placeholder="0"
                min="-100"
                max="100"
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.adequatelyStocked_q1 ? 'input-error' : ''}
              />
              {validationErrors.adequatelyStocked_q1 && (
                <span className="validation-error">{validationErrors.adequatelyStocked_q1}</span>
              )}
            </div>
            <div className="quarter-input-group">
              <label>Q2%</label>
              <input 
                type="number" 
                value={stockLevels.adequatelyStocked.q2}
                onChange={(e) => handleStockLevelChange('adequatelyStocked', 'q2', e.target.value)}
                placeholder="0"
                min="-100"
                max="100"
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.adequatelyStocked_q2 ? 'input-error' : ''}
              />
              {validationErrors.adequatelyStocked_q2 && (
                <span className="validation-error">{validationErrors.adequatelyStocked_q2}</span>
              )}
            </div>
            <div className="quarter-input-group">
              <label>Q3%</label>
              <input 
                type="number" 
                value={stockLevels.adequatelyStocked.q3}
                onChange={(e) => handleStockLevelChange('adequatelyStocked', 'q3', e.target.value)}
                placeholder="0"
                min="-100"
                max="100"
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.adequatelyStocked_q3 ? 'input-error' : ''}
              />
              {validationErrors.adequatelyStocked_q3 && (
                <span className="validation-error">{validationErrors.adequatelyStocked_q3}</span>
              )}
            </div>
            <div className="quarter-input-group">
              <label>Q4%</label>
              <input 
                type="number" 
                value={stockLevels.adequatelyStocked.q4}
                onChange={(e) => handleStockLevelChange('adequatelyStocked', 'q4', e.target.value)}
                placeholder="0"
                min="-100"
                max="100"
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.adequatelyStocked_q4 ? 'input-error' : ''}
              />
              {validationErrors.adequatelyStocked_q4 && (
                <span className="validation-error">{validationErrors.adequatelyStocked_q4}</span>
              )}
            </div>
          </div>
        </div>

        {/* Over Stock Section */}
        <div className="stock-section">
          <label className="stock-section-label">Over Stock</label>
          <div className="quarter-inputs">
            <div className="quarter-input-group">
              <label>Q1%</label>
              <input 
                type="number" 
                value={stockLevels.overStock.q1}
                onChange={(e) => handleStockLevelChange('overStock', 'q1', e.target.value)}
                placeholder="0"
                min="-100"
                max="100"
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.overStock_q1 ? 'input-error' : ''}
              />
              {validationErrors.overStock_q1 && (
                <span className="validation-error">{validationErrors.overStock_q1}</span>
              )}
            </div>
            <div className="quarter-input-group">
              <label>Q2%</label>
              <input 
                type="number" 
                value={stockLevels.overStock.q2}
                onChange={(e) => handleStockLevelChange('overStock', 'q2', e.target.value)}
                placeholder="0"
                min="-100"
                max="100"
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.overStock_q2 ? 'input-error' : ''}
              />
              {validationErrors.overStock_q2 && (
                <span className="validation-error">{validationErrors.overStock_q2}</span>
              )}
            </div>
            <div className="quarter-input-group">
              <label>Q3%</label>
              <input 
                type="number" 
                value={stockLevels.overStock.q3}
                onChange={(e) => handleStockLevelChange('overStock', 'q3', e.target.value)}
                placeholder="0"
                min="-100"
                max="100"
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.overStock_q3 ? 'input-error' : ''}
              />
              {validationErrors.overStock_q3 && (
                <span className="validation-error">{validationErrors.overStock_q3}</span>
              )}
            </div>
            <div className="quarter-input-group">
              <label>Q4%</label>
              <input 
                type="number" 
                value={stockLevels.overStock.q4}
                onChange={(e) => handleStockLevelChange('overStock', 'q4', e.target.value)}
                placeholder="0"
                min="-100"
                max="100"
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.overStock_q4 ? 'input-error' : ''}
              />
              {validationErrors.overStock_q4 && (
                <span className="validation-error">{validationErrors.overStock_q4}</span>
              )}
            </div>
          </div>
        </div>

        {/* Limit Percentage Section */}
        <div className="stock-section">
          <label className="stock-section-label">Limit Percentage (%)</label>
          <div className="quarter-inputs">
            <div className="quarter-input-group">
              <label>Negative</label>
              <input 
                type="number" 
                placeholder="0"
                min="-100"
                max="0"
                value={stockLevels.limits.percentageMin}
                onChange={(e) => handleStockLevelChange('limits', 'percentageMin', e.target.value)}
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.limits_percentageMin ? 'input-error' : ''}
              />
              {validationErrors.limits_percentageMin && (
                <span className="validation-error">{validationErrors.limits_percentageMin}</span>
              )}
            </div>
            <div className="quarter-input-group">
              <label>Positive</label>
              <input 
                type="number" 
                placeholder="0"
                min="0"
                max="100"
                value={stockLevels.limits.percentageMax}
                onChange={(e) => handleStockLevelChange('limits', 'percentageMax', e.target.value)}
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.limits_percentageMax ? 'input-error' : ''}
              />
              {validationErrors.limits_percentageMax && (
                <span className="validation-error">{validationErrors.limits_percentageMax}</span>
              )}
            </div>
          </div>
        </div>

        {/* Limit Price Section */}
        <div className="stock-section">
          <label className="stock-section-label">Limit Price ($)</label>
          <div className="quarter-inputs">
            <div className="quarter-input-group">
              <label>Negative</label>
              <input 
                type="number" 
                placeholder="0"
                min="-500"
                max="0"
                value={stockLevels.limits.priceMin}
                onChange={(e) => handleStockLevelChange('limits', 'priceMin', e.target.value)}
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.limits_priceMin ? 'input-error' : ''}
              />
              {validationErrors.limits_priceMin && (
                <span className="validation-error">{validationErrors.limits_priceMin}</span>
              )}
            </div>
            <div className="quarter-input-group">
              <label>Positive</label>
              <input 
                type="number" 
                placeholder="0"
                min="0"
                max="500"
                value={stockLevels.limits.priceMax}
                onChange={(e) => handleStockLevelChange('limits', 'priceMax', e.target.value)}
                disabled={!isEditing || !canEditGlobalConfig}
                className={validationErrors.limits_priceMax ? 'input-error' : ''}
              />
              {validationErrors.limits_priceMax && (
                <span className="validation-error">{validationErrors.limits_priceMax}</span>
              )}
            </div>
          </div>
        </div>

        {/* ML Configuration Section */}
        <div className="stock-section">
          <label className="stock-section-label">ML Configuration</label>
          <div className="quarter-inputs">
            <div className="quarter-input-group">
              <label>Minimum Data Points For Regration</label>
                             <input 
                 type="number" 
                 placeholder="0"
                 min="0"
                 max="5"
                 value={minimumDataPoints}
                 onChange={(e) => handleMinimumDataPointsChange(e.target.value)}
                 disabled={!isEditing || !canEditGlobalConfig}
                 className={validationErrors.minimumDataPoints ? 'input-error' : ''}
               />
              {validationErrors.minimumDataPoints && (
                <span className="validation-error">{validationErrors.minimumDataPoints}</span>
              )}
            </div>
                         <div className="quarter-input-group">
               <label>ML Model Type</label>
               <select 
                 className="select-type-dropdown"
                 value={mlModelType}
                 onChange={(e) => handleMlModelTypeChange(e.target.value)}
                 disabled={!isEditing || !canEditGlobalConfig}
               >
                 <option value="">Select ML Model Type...</option>
                 {modelTypes.map((model, index) => (
                   <option key={index} value={model.model_name}>
                     {model.model_name}
                   </option>
                 ))}
               </select>
             </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <PermissionGuard 
            permission={PERMISSION_CODES.GLOBLE_CONFIG_EDIT}
          >
            <button 
              className="btn-edit" 
              onClick={handleEdit}
              disabled={isEditing}
            >
              Edit
            </button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSION_CODES.GLOBLE_CONFIG_EDIT}>
            <button 
              className="btn-save" 
              onClick={handleSave}
              disabled={!isEditing}
            >
              Save
            </button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSION_CODES.GLOBLE_CONFIG_EDIT}>
            <button 
              className="btn-cancel" 
              onClick={handleCancel}
              disabled={!isEditing}
            >
              Cancel
            </button>
          </PermissionGuard>
        </div>
      </div>
    </div>
  );
};

export default GlobalConfigurationPage;
